import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';

import { PublicLayout } from '@/components/layout/public-layout';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { TextField } from '@/components/ui/text-field';
import { trackEvent } from '@/lib/analytics';
import { EMAIL_PATTERN, PHONE_PATTERN, formatPhone } from '@/lib/validation';

import { captureArrival } from '../arrival';
import { readDraft, writeDraft, type SignUpDraft } from '../signup-draft';
import { FORM_ERROR } from '../use-auth-error';

const ASSURANCES = [
  'Takes about 3 minutes',
  'No résumé needed to get started',
  'A dedicated recruiter calls you',
] as const;

/** Screen one of sign-up: who you are and how to reach you. Nothing is sent yet. */
export function StartPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();

  useEffect(() => {
    captureArrival(params);
  }, [params]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitted },
  } = useForm<SignUpDraft>({
    defaultValues: readDraft() ?? { firstName: '', lastName: '', phone: '', email: '' },
  });

  const submit = (v: SignUpDraft) => {
    writeDraft({
      firstName: v.firstName.trim(),
      lastName: v.lastName.trim(),
      phone: v.phone,
      email: v.email.trim(),
    });
    trackEvent('signup_started');
    void navigate({ pathname: '/welcome/finish', search: location.search });
  };

  const hasFieldErrors = isSubmitted && Object.keys(errors).length > 0;

  return (
    <PublicLayout
      title="Start your journey with MAS."
      copy="Ready for your next adventure? You're just a few clicks away from an exciting new opportunity with MAS."
      hero={
        <ul className="assurances">
          {ASSURANCES.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      }
    >
      <div>
        <h3>Start your application</h3>
        <p className="muted">Tell us how to reach you and we&apos;ll take it from there.</p>
      </div>
      {hasFieldErrors && <Alert>{FORM_ERROR}</Alert>}

      <form className="stack" noValidate onSubmit={(e) => void handleSubmit(submit)(e)}>
        <TextField
          id="start-firstName"
          label="First Name"
          autoComplete="given-name"
          error={errors.firstName?.message}
          {...register('firstName', { required: 'Please provide your first name' })}
        />
        <TextField
          id="start-lastName"
          label="Last Name"
          autoComplete="family-name"
          error={errors.lastName?.message}
          {...register('lastName', { required: 'Please provide your last name' })}
        />
        <TextField
          id="start-phone"
          label="Mobile Phone"
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          placeholder="(555) 555-5555"
          error={errors.phone?.message}
          {...register('phone', {
            required: 'Please provide a valid phone number',
            pattern: { value: PHONE_PATTERN, message: 'Please provide a valid phone number' },
            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
              setValue('phone', formatPhone(e.target.value), { shouldValidate: isSubmitted });
            },
          })}
        />
        <TextField
          id="start-email"
          label="Email Address"
          type="email"
          inputMode="email"
          autoComplete="email"
          error={errors.email?.message}
          {...register('email', {
            required: 'Please provide a valid email address',
            pattern: { value: EMAIL_PATTERN, message: 'Please provide a valid email address' },
          })}
        />
        <div className="form__actions form__actions--sticky">
          <Button type="submit" block>
            Continue
          </Button>
        </div>
      </form>

      <hr className="divider divider--gray" />
      <div className="card__switch">
        <p>Already started an application?</p>
        <Link
          to={{ pathname: '/sign-in', search: location.search }}
          className="btn btn--secondary btn--block"
        >
          Pick up where you left off
        </Link>
      </div>
    </PublicLayout>
  );
}
